import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { parse, ParseResult, unparse } from 'papaparse';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { JsonPipe } from '@angular/common';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';

interface ITranslation {
    labelCode: string;
    languageCode: string;
    translation?: string;
}

interface ITranslationModel extends ITranslation {
    translationMissing: boolean;
}

@Component({
    selector: 'my-package',
    styleUrls: ["./localisation.component.css"],
    templateUrl: './localisation.component.html',
    imports: [MatButton, MatIcon, MatInput, FormsModule, JsonPipe]
})

export class LocalisationComponent implements OnInit {
    private translationsUnmodified: ITranslation[];
    private newTranslation: ITranslation | {};
    public translationsModel: ITranslationModel[];
    public error: any[];

    constructor(private http: HttpClient) {
        this.translationsModel = [];
        this.error = [];
        this.newTranslation = {};
    }

    private match(translation: ITranslation) {
        return function (t: ITranslation) {
            return t.labelCode == translation.labelCode && t.languageCode == translation.languageCode;
        }
    }

    handleFileInput(files: FileList) {
        const instance = this;
        this.error = [];
        function UpdateTranslations(contents: ParseResult<ITranslation>) {

            const { data, meta, errors } = contents;

            if (errors.length) {
                instance.error.concat(errors);
                return;
            }

            if (!meta.fields || !meta.fields.length) {
                instance.error.push("The CSV should contain a header row");
            }

            for (const header of ['labelCode', 'languageCode', 'translation']) {
                if (meta.fields.indexOf(header) === -1) {
                    instance.error.push(`The CSV should contain a header called: ${header}`);
                }
            }

            if (instance.error.length) {
                return;
            }

            const newData: ITranslation[] = data
                .filter(r => r.labelCode && r.languageCode && r.translation);

            instance.updateLocalisations(newData, instance);
        }

        for (var i = 0; i < files.length; i++) {
            parse(files.item(i), { header: true, complete: UpdateTranslations });
        }
    }

    private updateLocalisations(newData: ITranslation[], instance: LocalisationComponent) {
        instance.translationsModel = instance.translationsModel
            .filter(r => !newData.some(instance.match(r)))
            .concat(newData.map(d => ({ ...d, translationMissing: false })))
            .sort(instance.translationSort);

        const result = {};
        for (var i = 0; i < newData.length; i++) {
            var current = newData[i];
            if (!result[current.labelCode])
                result[current.labelCode] = {};

            if (result[current.labelCode][current.languageCode])
                result[current.labelCode][current.languageCode].push(current);
            else
                result[current.labelCode][current.languageCode] = [current];
        }

        const duplicates = [];

        for (var labelCode in result)
            for (var languageCode in result[labelCode])
                if (result[labelCode][languageCode].length > 1)
                    duplicates.push(result[labelCode][languageCode]);

        if (duplicates.length > 0) {
            instance.catch({
                Message: "Duplicate translations were uploaded",
                Duplicates: duplicates
            }, instance);
            return;
        }

        instance.http
            .put("/api/passport/translations", newData)
            .toPromise()
            .then(() => instance.refreshData(instance))
            .catch(e => instance.catch(e, instance));
    }

    download() {
        const filename = "translations.csv";
        var blob = new Blob([unparse(this.translationsModel)], { type: 'text/csv' });

        var elem = window.document.createElement('a');
        elem.href = window.URL.createObjectURL(blob);
        elem.download = filename;
        document.body.appendChild(elem);
        elem.click();
        document.body.removeChild(elem);
    }

    addLocalisation() {
        if (!this.newTranslationIsValid())
            return;
        this.updateLocalisation(this.newTranslation as ITranslation);
        this.newTranslation = {};
    }

    updateLocalisation(translation: ITranslation) {
        this.updateLocalisations([translation], this);
    }

    private translationSort(translationA: ITranslation, translationB: ITranslation) {
        const properties = ['labelCode', 'languageCode', 'translation']
        for (var i = 0; i < properties.length; i++) {
            var result = (translationA[properties[i]] as string).localeCompare(translationB[properties[i]] as string, undefined, { sensitivity: 'base' });
            if (result)
                return result;
        }
        return 0;
    }

    newTranslationIsValid() {
        const translation = this.newTranslation as ITranslation;
        return (translation.labelCode && translation.languageCode && translation.translation);
    }

    ngOnInit(): void {
        this.refreshData(this);
    }

    hasModification(translation: ITranslation) {
        const unmodified = this.translationsUnmodified.filter(this.match(translation));
        if (unmodified.length === 0)
            return translation.translation && translation.translation.length > 0;
        return unmodified[0].translation !== translation.translation;
    }

    reset(translation: ITranslation) {
        translation.translation = this.translationsUnmodified.filter(this.match(translation))[0].translation;
    }

    refreshData(instance: LocalisationComponent) {
        function getMissingTranslations(translations: ITranslation[]): ITranslationModel[] {
            const allLanguages = [];
            const allLabels = [];
            for (var i = 0; i < translations.length; i++) {
                const { languageCode, labelCode } = translations[i];
                if (allLanguages.filter(l => l === languageCode).length === 0)
                    allLanguages.push(languageCode);
                if (allLabels.filter(l => l === labelCode).length === 0)
                    allLabels.push(labelCode);
            }

            const result: ITranslationModel[] = [];
            for (var i = 0; i < allLanguages.length; i++) {
                const language = allLanguages[i];
                for (var j = 0; j < allLabels.length; j++) {
                    const label = allLabels[j];
                    if (translations.filter(t => t.labelCode === label && t.languageCode === language).length === 0)
                        result.push({ languageCode: language, labelCode: label, translation: undefined, translationMissing: true });
                }
            }

            return result;
        }

        instance.http
            .get("/api/passport/translations")
            .toPromise()
            .then((result: ITranslation[]) => {
                function isModified(translation: ITranslation) {
                    const previousValue = instance.translationsUnmodified.filter(instance.match(translation));
                    if (previousValue.length === 0)
                        return false;
                    return previousValue[0].translation !== translation.translation;
                }

                const modified = instance.translationsModel.filter(isModified);

                const modifiedAndUnmodified = modified
                    .concat(result.filter(r => modified.filter(instance.match(r)).length === 0).map(r => ({ ...r, translationMissing: false })));

                instance.translationsModel = modifiedAndUnmodified
                    .concat(getMissingTranslations(modifiedAndUnmodified))
                    .sort(instance.translationSort);

                instance.translationsUnmodified = [...result.map(t => ({ ...t }))];
            })
            .catch(e => instance.catch(e, instance));
    }

    private catch(e: any, instance: LocalisationComponent) {
        if (e) {
            instance.error.push(e);
            console.error(e);
        }
    }
}